<template>
  <section v-if="hasAnyTrip" class="dq-rail" :class="severity" data-test="data-quality-rail">
    <div class="dq-headline">
      <span class="dq-severity-dot" :class="severity" aria-hidden="true" />
      <h2 class="dq-title">{{ title }}</h2>
      <span v-if="lowSignal" class="dq-pill warn" data-test="dq-low-signal">Low signal</span>
    </div>

    <div
      v-if="unrecognizedStatuses.length > 0"
      class="dq-unrecognized"
      role="alert"
      data-test="dq-unrecognized"
    >
      <p class="dq-unrecognized-title">Unrecognized completion statuses</p>
      <p class="dq-unrecognized-body">
        The status values you are using ({{ unrecognizedStatusList }}) are not
        recognized by the scorecard as "delivered". Completion-based metrics
        (M1, M6, M7, M19) will under-report until your workflow is mapped.
        <strong>Please contact the AI team to add support for your workflow values.</strong>
      </p>
    </div>

    <ul class="dq-grid">
      <li v-if="coverageTrip" class="dq-tile" data-test="dq-coverage">
        <span class="dq-label">Estimate coverage</span>
        <span class="dq-value">{{ coveragePctText }}</span>
        <span class="dq-sub">{{ coverage?.sized_count ?? 0 }} of {{ coverage?.total_count ?? 0 }} sized</span>
      </li>
      <li v-if="readinessTrip" class="dq-tile" data-test="dq-readiness">
        <span class="dq-label">Sprint start readiness</span>
        <span class="dq-value bad">Not ready</span>
        <span class="dq-sub">tickets entered without SP / priority / work-type</span>
      </li>
      <li v-if="addsTrip" class="dq-tile" data-test="dq-mid-sprint">
        <span class="dq-label">Mid-sprint additions</span>
        <span class="dq-value">{{ addsPctText }}</span>
        <span class="dq-sub">scope changed after sprint start</span>
      </li>
      <li v-if="discrepancyTrip" class="dq-tile" data-test="dq-discrepancy">
        <span class="dq-label">Field/changelog mismatch</span>
        <span class="dq-value">{{ discrepancyCount }}</span>
        <span class="dq-sub">tickets where Unplanned tag disagrees with changelog</span>
      </li>
      <li v-if="excludedTrip" class="dq-tile" data-test="dq-excluded">
        <span class="dq-label">Cycle-time excluded</span>
        <span class="dq-value">{{ excludedCount }}</span>
        <span class="dq-sub">delivered tickets missing Start / End date</span>
      </li>
      <li v-if="escalationTrip" class="dq-tile" data-test="dq-escalations">
        <span class="dq-label">Escalations in flight</span>
        <span class="dq-value">{{ escalationCount }}</span>
        <span class="dq-sub">unplanned firefighting</span>
      </li>
      <li v-if="subtaskTrip" class="dq-tile" data-test="dq-subtasks">
        <span class="dq-label">Sub-task share</span>
        <span class="dq-value">{{ subtaskShareText }}</span>
        <span class="dq-sub">{{ subtaskCount }} of {{ totalCount }} sprint items are sub-tasks</span>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  CoverageSummary,
  MetricEnvelope,
  SprintDetail,
  UnrecognizedTerminalStatus,
} from "@/api/types";

const props = defineProps<{ sprint: SprintDetail }>();

const metrics = computed(() => props.sprint.metrics ?? {});

function envValue<T>(name: string): T | null | undefined {
  const env = (metrics.value as Record<string, MetricEnvelope>)[name];
  if (!env || typeof env !== "object") return null;
  return (env as MetricEnvelope).value as T | null | undefined;
}

const coverage = computed<CoverageSummary | undefined>(() => metrics.value.coverage);
const lowSignal = computed(() => Boolean(metrics.value.low_signal));
const totalCount = computed(() => coverage.value?.total_count ?? 0);
const subtaskCount = computed(() => Number(metrics.value.subtask_count ?? 0));
const escalationCount = computed(() => Number(metrics.value.escalation_count ?? 0));

const m17 = computed(() => envValue<number>("M17_missing_estimates_pct"));
const m18 = computed(() => envValue<boolean>("M18_start_readiness"));
const m16 = computed(() => envValue<number>("M16_mid_sprint_additions_pct"));
const discrepancyCount = computed(() =>
  Number((metrics.value as Record<string, unknown>).m16_field_recompute_discrepancy ?? 0),
);
const excludedCount = computed(() =>
  Number((metrics.value as Record<string, unknown>).m19_excluded ?? 0),
);

// Tripwires — each surfaces the tile only when worth a coaching conversation.
const coverageTrip = computed(() =>
  coverage.value != null && coverage.value.pct != null && coverage.value.pct < 0.9,
);
const readinessTrip = computed(() => m18.value === false);
const addsTrip = computed(() => (m17.value != null) && (m16.value ?? 0) >= 0.2);
const discrepancyTrip = computed(() => discrepancyCount.value > 0);
const excludedTrip = computed(() => excludedCount.value > 0);
const escalationTrip = computed(() => escalationCount.value > 0);
const subtaskTrip = computed(() => totalCount.value > 0 && subtaskCount.value / totalCount.value >= 0.25);

const unrecognizedStatuses = computed<UnrecognizedTerminalStatus[]>(
  () => metrics.value.unrecognized_terminal_statuses ?? [],
);
const unrecognizedStatusList = computed(() =>
  unrecognizedStatuses.value
    .map((u) => `"${u.status}" (${u.count})`)
    .join(", "),
);

const hasAnyTrip = computed(() =>
  lowSignal.value
  || coverageTrip.value
  || readinessTrip.value
  || addsTrip.value
  || discrepancyTrip.value
  || excludedTrip.value
  || escalationTrip.value
  || subtaskTrip.value
  || unrecognizedStatuses.value.length > 0,
);

// Severity ladder: red when low_signal or coverage < 50% or workflow is
// unmapped (numbers are not trustworthy). Amber for any single tripwire.
// Below that the rail wouldn't render at all.
const severity = computed(() => {
  if (lowSignal.value) return "red";
  if (coverage.value?.pct != null && coverage.value.pct < 0.5) return "red";
  if (unrecognizedStatuses.value.length > 0) return "red";
  if (m18.value === false) return "amber";
  return "amber";
});

const title = computed(() => {
  if (severity.value === "red") return "Read these numbers with caution";
  return "Hygiene watch";
});

function fmtPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}

const coveragePctText = computed(() => fmtPct(coverage.value?.pct ?? null));
const addsPctText = computed(() => fmtPct(m16.value ?? null));
const subtaskShareText = computed(() =>
  totalCount.value === 0 ? "—" : `${Math.round((subtaskCount.value / totalCount.value) * 100)}%`,
);
</script>

<style scoped>
.dq-rail {
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left-width: 4px;
  border-radius: 8px;
  padding: 14px 16px;
  background: var(--surface-card, #fff);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.dq-rail.amber { border-left-color: #d97706; background: #fffbeb; }
.dq-rail.red { border-left-color: #dc2626; background: #fef2f2; }

.dq-headline { display: flex; align-items: center; gap: 10px; }
.dq-severity-dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
.dq-severity-dot.amber { background: #d97706; }
.dq-severity-dot.red { background: #dc2626; }
.dq-title { margin: 0; font-size: 14px; font-weight: 600; color: var(--text-color, #111827); }
.dq-pill {
  font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px;
  border: 1px solid currentColor; text-transform: uppercase; letter-spacing: 0.04em;
}
.dq-pill.warn { color: #b45309; background: #fef3c7; border-color: #f59e0b; }

.dq-unrecognized {
  background: #fff;
  border: 1px solid #fca5a5;
  border-left: 4px solid #dc2626;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex; flex-direction: column; gap: 4px;
}
.dq-unrecognized-title { margin: 0; font-size: 12px; font-weight: 700; color: #991b1b; text-transform: uppercase; letter-spacing: 0.04em; }
.dq-unrecognized-body { margin: 0; font-size: 13px; line-height: 1.5; color: var(--text-color, #111827); }
.dq-unrecognized-body strong { color: #991b1b; }

.dq-grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
.dq-tile {
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 6px;
  padding: 8px 10px;
  display: flex; flex-direction: column; gap: 2px;
}
.dq-label { font-size: 11px; color: var(--text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.04em; }
.dq-value { font-size: 18px; font-weight: 600; color: var(--text-color, #111827); }
.dq-value.bad { color: #b91c1c; }
.dq-sub { font-size: 11px; color: var(--text-muted, #6b7280); }
</style>
